import { Component, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import {
  Button,
  SimpleDialog,
  SimpleDialogLayout,
  InputLabel,
  Select,
  Textarea,
  ConfirmService,
} from 'excellence-ui';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { form, FormField, required } from '@angular/forms/signals';
import { HttpError } from '../../../../core/clients/omni-core/models';
import { HttpErrorComponent } from '../../../../shared/components/http-error/http-error';
import { Api } from '../../../../core/clients/omni-core/api';
import { createConsultantRequest } from '../../../../core/clients/omni-core/functions';
import { environment } from '../../../../../environments/environment';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { OrganizationsStore } from '../../../organizations/store/organizations.store';
import { IsNotDemoModeDirective } from '../../../demo/directives/is-not-demo-mode';

@Component({
  selector: 'app-dashboard-consultant-card',
  imports: [
    NgIcon,
    Button,
    SimpleDialog,
    SimpleDialogLayout,
    InputLabel,
    Select,
    FieldErrors,
    FormField,
    Textarea,
    HttpErrorComponent,
    TranslocoPipe,
    IsNotDemoModeDirective,
  ],
  templateUrl: './dashboard-consultant-card.html',
})
export class DashboardConsultantCard {
  private api = inject(Api);
  private organizationsStore = inject(OrganizationsStore);
  private confirmService = inject(ConfirmService);
  private translocoService = inject(TranslocoService);

  organizationId = input.required<string>();
  initWithOpenContactDialog = input.required<boolean>();

  protected readonly links = environment.links;

  private orgaConsultant = computed(
    () =>
      this.organizationsStore.entities()[this.organizationId()]?.data?.personalConsultant ??
      'SIMON_ZOELLER',
  );
  protected consultantData = computed(() => environment.personalConsultants[this.orgaConsultant()]);

  private dialog = viewChild(SimpleDialog);

  private contactData = signal({
    subject: '',
    message: '',
  });

  protected contactForm = form(this.contactData, (schemaPath) => {
    required(schemaPath.subject);
    required(schemaPath.message);
  });

  protected sendContactEmailState = signal<{
    isLoading: boolean;
    error: HttpError | unknown | null;
  }>({
    isLoading: false,
    error: null,
  });

  constructor() {
    effect(() => {
      if (this.initWithOpenContactDialog()) {
        this.dialog()?.showModal();
      }
    });
  }

  protected onSubmit() {
    if (this.sendContactEmailState().isLoading || !this.contactForm().valid()) {
      this.contactForm.subject().markAsTouched();
      this.contactForm.message().markAsTouched();
      return;
    }
    this.sendContactEmail();
  }

  private async sendContactEmail() {
    this.sendContactEmailState.set({
      isLoading: true,
      error: null,
    });
    const data = this.contactForm().value();
    try {
      await this.api.invoke(createConsultantRequest, {
        oid: this.organizationId(),
        body: {
          content: data.message,
          topic: data.subject,
        },
      });
      this.sendContactEmailState.set({
        isLoading: false,
        error: null,
      });
      this.onSendContactEmailSuccess();
    } catch (error) {
      this.sendContactEmailState.set({
        isLoading: false,
        error,
      });
    }
  }

  private onSendContactEmailSuccess() {
    this.dialog()?.close();
    this.confirmService.confirm({
      title: this.translocoService.translate(
        'cockpit.dashboard.consultantCard.successNotification.title',
      ),
      message: this.translocoService.translate(
        'cockpit.dashboard.consultantCard.successNotification.message',
      ),
      allowCancel: false,
      confirmButtonText: this.translocoService.translate(
        'cockpit.dashboard.consultantCard.successNotification.confirmButtonText',
      ),
      icon: 'heroInformationCircle',
    });
    this.contactData.set({
      subject: '',
      message: '',
    });
    this.contactForm().reset();
  }

  async onClickCalendar() {
    if (
      !(await this.confirmService.confirm({
        allowCancel: true,
        cancelButtonText: this.translocoService.translate(
          'cockpit.dashboard.consultantCard.calendarNotification.cancelButtonText',
        ),
        confirmButtonText: this.translocoService.translate(
          'cockpit.dashboard.consultantCard.calendarNotification.confirmButtonText',
        ),
        title: this.translocoService.translate(
          'cockpit.dashboard.consultantCard.calendarNotification.title',
          { name: this.consultantData().externalCalendarName },
        ),
        message: this.translocoService.translate(
          'cockpit.dashboard.consultantCard.calendarNotification.message',
          { link: this.consultantData().externalCalendarLinkPrivacyPolicy },
        ),
        icon: 'heroCalendar',
      }))
    ) {
      return;
    }
    window.open(this.consultantData().externalCalendarLink, '_blank', 'noopener,noreferrer');
  }
}
